import { batch, createSignal } from "solid-js";

import { error as logError } from "../../../utils/console";
import {
  fetchDailyTrainingSettings,
  type SummaryTablesHistoryFilters,
} from "./dailyTrainingSettingsApi";

/** Everything the four debrief queries need. Null means "not ready — do not fetch". */
export interface FleetDebriefQuery {
  className: string;
  projectId: number;
  /** Single live/report day, YYYYMMDD. Used as both startDate and endDate. */
  dateYmd: string;
  liveOnly: boolean;
  sourceNames: string[];
  filters: SummaryTablesHistoryFilters;
  twsBin: number | null;
  /** ISO lower/upper bound from the follow window or a manual timeline brush. */
  brushStartTime?: string | null;
  brushEndTime?: string | null;
}

type Rows = Record<string, unknown>[];

/**
 * The Fleet Debrief's four queries: upwind + downwind straight line, and tack + gybe maneuvers.
 * Shared by the live FLEET SUMMARY page and the /tools/fleet-debrief pop-out so both issue
 * identical requests.
 *
 * Partial failures are warn-but-render: whichever sections returned still display, and `error`
 * carries the rest. A request sequence number drops stale responses, so dragging the timeline
 * brush cannot let an earlier, slower query overwrite a later one.
 */
export function useFleetDebriefData() {
  const [uwRows, setUwRows] = createSignal<Rows>([]);
  const [dwRows, setDwRows] = createSignal<Rows>([]);
  const [tackRows, setTackRows] = createSignal<Rows>([]);
  const [gybeRows, setGybeRows] = createSignal<Rows>([]);
  const [twsBins, setTwsBins] = createSignal<number[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");

  let fetchSeq = 0;

  const load = async (query: FleetDebriefQuery): Promise<void> => {
    const seq = ++fetchSeq;
    setLoading(true);

    const common = {
      className: query.className,
      projectId: query.projectId,
      view: "fleet" as const,
      speedUnit: "kph" as const,
      startDate: query.dateYmd,
      endDate: query.dateYmd,
      filters: query.filters,
      sourceNames: query.sourceNames,
      liveOnly: query.liveOnly,
      twsBin: query.twsBin,
      brushStartTime: query.brushStartTime ?? null,
      brushEndTime: query.brushEndTime ?? null,
    };

    try {
      const [uw, dw, tack, gybe] = await Promise.all([
        fetchDailyTrainingSettings({ ...common, legType: "upwind", maneuverType: "tack", sections: "straight" }),
        fetchDailyTrainingSettings({ ...common, legType: "downwind", maneuverType: "tack", sections: "straight" }),
        fetchDailyTrainingSettings({ ...common, legType: "upwind", maneuverType: "tack", sections: "maneuvers" }),
        fetchDailyTrainingSettings({ ...common, legType: "downwind", maneuverType: "gybe", sections: "maneuvers" }),
      ]);

      // A newer load started while these were in flight — its results win.
      if (seq !== fetchSeq) { return; }

      const failures = [uw, dw, tack, gybe].filter((r) => !r.ok);
      batch(() => {
        setUwRows(uw.ok ? uw.data.straight_line_rows : []);
        setDwRows(dw.ok ? dw.data.straight_line_rows : []);
        setTackRows(tack.ok ? tack.data.maneuver_rows : []);
        setGybeRows(gybe.ok ? gybe.data.maneuver_rows : []);
        if (uw.ok && uw.data.tws_bins.length > 0) { setTwsBins(uw.data.tws_bins); }
        setError(
          failures.length > 0
            ? failures.map((f) => (f.ok ? "" : f.message)).filter(Boolean).join("; ")
            : ""
        );
      });
    } catch (err) {
      if (seq !== fetchSeq) { return; }
      logError("useFleetDebriefData: load failed", err);
      setError(err instanceof Error ? err.message : "Failed to load fleet debrief data");
    } finally {
      if (seq === fetchSeq) { setLoading(false); }
    }
  };

  return { uwRows, dwRows, tackRows, gybeRows, twsBins, loading, error, load };
}
